import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, TrendingUp, Loader2, Mail, Plus, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';

/**
 * Reusable dialog for reporting short/excess quantity.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - sourceType: "challan" | "purchase_receipt"
 *  - sourceId: string (challan_id or pr_id)
 *  - sourceNumber: string (challan_number or po_number)
 *  - partyType: "customer" | "supplier"
 *  - partyName: string
 *  - partyId: string
 *  - partyEmail: string (default email)
 *  - items: [{ item_id, item_name, item_unique_id, expected_qty, received_qty, unit_of_measure, godown_id }]
 *  - invoiceNumber: string (optional)
 *  - invoiceDate: string (optional)
 */
export function QtyDiscrepancyDialog({
  open, onClose, sourceType, sourceId, sourceNumber,
  partyType, partyName, partyId, partyEmail,
  items = [], invoiceNumber = '', invoiceDate = '', godowns = [],
}) {
  const [discItems, setDiscItems] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [invNumber, setInvNumber] = useState(invoiceNumber);
  const [invDate, setInvDate] = useState(invoiceDate);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (open && items.length > 0) {
      setDiscItems(items.map(it => ({
        ...it,
        discrepancy_qty: '',
        discrepancy_type: 'short',
        godown_id: it.godown_id || '',
      })));
      setResult(null);
      setRemarks('');
      setInvNumber(invoiceNumber);
      setInvDate(invoiceDate);
      if (partyEmail) {
        setEmailRecipients([partyEmail]);
      } else {
        setEmailRecipients([]);
      }
      setNewEmail('');
    }
  }, [open, items, invoiceNumber, invoiceDate, partyEmail]);

  const updateDiscItem = (idx, field, value) => {
    setDiscItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const hasDiscrepancies = discItems.some(it => parseFloat(it.discrepancy_qty) > 0);

  const handleSubmit = async () => {
    if (!hasDiscrepancies) {
      toast.error('Enter at least one discrepancy quantity');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        source_type: sourceType,
        source_id: sourceId,
        source_number: sourceNumber,
        party_type: partyType,
        party_name: partyName,
        party_id: partyId || '',
        invoice_number: invNumber,
        invoice_date: invDate,
        remarks,
        items: discItems.filter(it => parseFloat(it.discrepancy_qty) > 0).map(it => ({
          item_id: it.item_id,
          item_name: it.item_name,
          item_unique_id: it.item_unique_id || '',
          expected_qty: parseFloat(it.expected_qty) || 0,
          received_qty: parseFloat(it.received_qty) || 0,
          discrepancy_qty: parseFloat(it.discrepancy_qty),
          discrepancy_type: it.discrepancy_type,
          unit_of_measure: it.unit_of_measure || 'Nos',
          godown_id: it.godown_id,
          ...(it.challan_line_index !== undefined && it.challan_line_index !== null
            ? { challan_line_index: it.challan_line_index }
            : {}),
        })),
      };
      const res = await inventoryAPI.reportQtyDiscrepancy(payload);
      setResult(res.data);
      const shortCount = payload.items.filter(i => i.discrepancy_type === 'short').length;
      const excessCount = payload.items.filter(i => i.discrepancy_type === 'excess').length;
      toast.success(`Discrepancy reported: ${shortCount} short, ${excessCount} excess. Stock adjusted.`);
    } catch (err) {
      showApiError(err, 'Failed to report discrepancy');
    } finally {
      setSubmitting(false);
    }
  };

  const addEmailRecipient = () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (emailRecipients.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setEmailRecipients(prev => [...prev, email]);
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setEmailRecipients(prev => prev.filter(e => e !== email));
  };

  const handleSendEmail = async () => {
    if (!result?.id) return;
    if (emailRecipients.length === 0) {
      toast.error('Add at least one email recipient');
      return;
    }
    setSendingEmail(true);
    try {
      await inventoryAPI.sendDiscrepancyEmail(result.id, { recipients: emailRecipients });
      toast.success(`Email sent to ${emailRecipients.length} recipient(s)`);
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const partyLabel = partyType === 'supplier' ? 'Supplier' : 'Customer';

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Report Short / Excess Quantity
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Reference info */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">Reference:</span>{' '}
                <span className="font-medium">{sourceNumber}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{partyLabel}:</span>{' '}
                <span className="font-medium">{partyName}</span>
              </div>
            </div>

            {/* Invoice details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px]">Invoice / DC Number</Label>
                <Input value={invNumber} onChange={e => setInvNumber(e.target.value)}
                  className="h-8 text-xs" placeholder="Invoice number" data-testid="disc-invoice-number" />
              </div>
              <div>
                <Label className="text-[10px]">Invoice / DC Date</Label>
                <Input type="date" value={invDate} onChange={e => setInvDate(e.target.value)}
                  className="h-8 text-xs" data-testid="disc-invoice-date" />
              </div>
            </div>

            {/* Item discrepancy entries */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Item Discrepancies</Label>
              {discItems.map((it, idx) => (
                <div key={idx} className="p-3 border rounded-lg bg-muted/20 space-y-2" data-testid={`disc-item-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">{it.item_name}</span>
                    <span className="text-[10px] text-muted-foreground">{it.item_unique_id}</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Expected</Label>
                      <Input value={it.expected_qty} readOnly className="h-7 text-xs bg-muted/50" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Received</Label>
                      <Input value={it.received_qty} readOnly className="h-7 text-xs bg-muted/50" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Type</Label>
                      <Select value={it.discrepancy_type} onValueChange={v => updateDiscItem(idx, 'discrepancy_type', v)}>
                        <SelectTrigger className="h-7 text-[10px]" data-testid={`disc-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="short">Short</SelectItem>
                          <SelectItem value="excess">Excess</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Disc. Qty</Label>
                      <Input type="number" step="0.01" min="0" value={it.discrepancy_qty}
                        onChange={e => updateDiscItem(idx, 'discrepancy_qty', e.target.value)}
                        className="h-7 text-xs" placeholder="0" data-testid={`disc-qty-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Godown</Label>
                      {godowns.length > 0 ? (
                        <Select value={it.godown_id} onValueChange={v => updateDiscItem(idx, 'godown_id', v)}>
                          <SelectTrigger className="h-7 text-[10px]" data-testid={`disc-godown-${idx}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {godowns.map(g => (
                              <SelectItem key={g.id} value={g.id}>{g.code || g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={it.godown_id ? 'Set' : 'Auto'} readOnly className="h-7 text-[10px] bg-muted/50" />
                      )}
                    </div>
                  </div>
                  {parseFloat(it.discrepancy_qty) > 0 && (
                    <p className="text-[10px] mt-1">
                      {it.discrepancy_type === 'short' ? (
                        <span className="text-red-600 font-medium">
                          <AlertTriangle className="w-3 h-3 inline mr-0.5" />
                          {it.discrepancy_qty} {it.unit_of_measure} will be deducted from stock
                        </span>
                      ) : (
                        <span className="text-emerald-600 font-medium">
                          <TrendingUp className="w-3 h-3 inline mr-0.5" />
                          {it.discrepancy_qty} {it.unit_of_measure} will be added to stock
                        </span>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div>
              <Label className="text-[10px]">Remarks</Label>
              <Textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                className="text-xs min-h-[60px]" placeholder="Additional details..." data-testid="disc-remarks" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || !hasDiscrepancies} size="sm" data-testid="submit-discrepancy-btn">
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />}
                Report & Adjust Stock
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* After submission — email step */
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
              <p className="font-medium mb-1">Discrepancy recorded & stock adjusted</p>
              {result.items?.map((it, idx) => (
                <p key={idx}>
                  {it.item_name}: <span className={it.discrepancy_type === 'short' ? 'text-red-600' : 'text-emerald-600'}>
                    {it.discrepancy_qty} {it.unit_of_measure} {it.discrepancy_type.toUpperCase()}
                  </span>
                  {it.stock_adjusted && ' (stock adjusted)'}
                </p>
              ))}
            </div>

            {/* Email section */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600" />
                Send Report to {partyLabel}
              </Label>
              <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                {emailRecipients.map(email => (
                  <Badge key={email} variant="secondary" className="text-[10px] gap-1 pr-1">
                    {email}
                    <button onClick={() => removeRecipient(email)} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addEmailRecipient()}
                  placeholder="Add email address..." className="h-8 text-xs flex-1" data-testid="disc-add-email" />
                <Button variant="outline" size="sm" onClick={addEmailRecipient} className="h-8">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} size="sm">Close (Don't Send)</Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail || emailRecipients.length === 0} size="sm"
                className="bg-blue-600 hover:bg-blue-700" data-testid="send-disc-email-btn">
                {sendingEmail ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send Email ({emailRecipients.length})
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
